import { useState } from 'react';
import {
  StepCta,
  StepHeading,
  StepSub,
  TertiaryLink,
} from '../../OnboardingShell/StepLayout';
import { useOnboardingV2 } from '../../OnboardingShell/onboardingV2';
import type {
  BodyUnit,
  OnboardingAnswers,
} from '../../../hooks/onboardingReducer';
import { heightProblem, weightProblem } from '../../../hooks/usePersistOnboarding';
import styles from './Step4BodyMetrics.module.css';

type Step4Props = {
  answers: OnboardingAnswers;
  setAnswer: (patch: Partial<OnboardingAnswers>) => void;
  onContinue: () => void;
};

/**
 * Step 4 — Body metrics (OPTIONAL).
 *
 * Imperial/metric pill toggle. Weight (lbs ↔ kg) + height (ft+in ↔
 * cm) inputs that swap based on the toggle. RED-S info panel below
 * explains why we ask. Skip path defers to onboarding chat agent
 * (existing pattern at api/index.js:1441 — chat agent gently asks
 * later if user skipped here).
 *
 * Continue is ALWAYS enabled (the step is optional). Both empty and
 * partially-filled states are valid — backend handles whatever it
 * gets.
 *
 * ⚖️ B439 (rulings 2026-10-21 item 12) — "optional" never meant "anything
 * goes". A value in a box must be one the completion write would KEEP: the
 * limits below come from the payload guards (`WEIGHT_*_RANGE`,
 * `HEIGHT_CM_RANGE` in `usePersistOnboarding`), not from the HTML attributes
 * that used to differ from them. A value outside the range, or badly formed,
 * is named in one sentence at the field once the athlete leaves the box (or
 * presses Continue), and Continue waits until it is fixed or cleared. The boxes
 * are text boxes with a number keypad, because a `type=number` box hides what
 * was typed ("1e2", a second point) from the code that has to name it.
 *
 * Persistence: this step only writes to the onboarding reducer (via
 * `setAnswer`). The values ride the SINGLE atomic completion POST at
 * Step 7 (`usePersistOnboarding` → `buildOnboardingPayload`, F-105),
 * projected to `_weight_kg`/`_height_cm`/units. Per the EC-4b lesson,
 * that projection OMITS every body-metric key when the step is skipped
 * or blank — it never writes a `0`/empty default over existing context
 * (regression-pinned in usePersistOnboarding.test.ts).
 *
 * The "Optional" badge in the rail row is rendered by OnboardingShell
 * (it reads step number; on step 4 the optional prop is true).
 */
export function Step4BodyMetrics({ answers, setAnswer, onContinue }: Step4Props) {
  // Part 7 (flag `onboarding_2_0_v1`): the waiting look of Continue.
  const v2 = useOnboardingV2();
  const unit: BodyUnit = answers.bodyUnit ?? 'imperial';

  const setUnit = (next: BodyUnit) => {
    if (next === unit) return;
    setAnswer({ bodyUnit: next });
  };

  // B439 — a sentence shows once the athlete has left that group of boxes (or
  // pressed Continue), then follows the value live. Never while typing a valid
  // number one digit at a time.
  const [shown, setShown] = useState({ weight: false, height: false });
  const weightErr = weightProblem(
    unit === 'metric' ? answers.weightKg : answers.weightLbs,
    unit,
  );
  const heightErr = heightProblem(
    { ft: answers.heightFt, inch: answers.heightIn, cm: answers.heightCm },
    unit,
  );

  const handleContinue = () => {
    if (weightErr || heightErr) {
      setShown({ weight: true, height: true });
      return;
    }
    onContinue();
  };

  const handleSkip = () => {
    // Ruling 4 (2026-10-22, B452): Skip clears ONLY a group that carries a
    // sentence (a value the completion write would refuse), so a skip never
    // saves a refused value the athlete walked away from. A value that is
    // valid stays and is saved: Skip used to wipe a weight and height she had
    // typed and been accepted for, which the screen never said. The chat
    // agent picks up whatever is left blank later (api/index.js:1441 pattern).
    // Only the units on screen are touched: the payload reads that unit alone.
    const cleared: Partial<OnboardingAnswers> = {};
    if (weightErr) {
      if (unit === 'metric') cleared.weightKg = undefined;
      else cleared.weightLbs = undefined;
    }
    if (heightErr) {
      if (unit === 'metric') {
        cleared.heightCm = undefined;
      } else {
        cleared.heightFt = undefined;
        cleared.heightIn = undefined;
      }
    }
    if (Object.keys(cleared).length > 0) setAnswer(cleared);
    onContinue();
  };

  return (
    <>
      <StepHeading>A few body details</StepHeading>
      <StepSub>
        Optional: unlocks weight-based nutrition advice, hydration
        targets and fueling checks.
      </StepSub>

      <div className={styles.unitToggle} role="group" aria-label="Units">
        <button
          type="button"
          className={[styles.unitBtn, unit === 'imperial' && styles.active].filter(Boolean).join(' ')}
          aria-pressed={unit === 'imperial'}
          onClick={() => setUnit('imperial')}
        >
          lbs / ft-in
        </button>
        <button
          type="button"
          className={[styles.unitBtn, unit === 'metric' && styles.active].filter(Boolean).join(' ')}
          aria-pressed={unit === 'metric'}
          onClick={() => setUnit('metric')}
        >
          kg / cm
        </button>
      </div>

      {/* Weight */}
      <div className={styles.row}>
        <label className={styles.label} htmlFor="ob-weight">
          Weight
        </label>
        <div className={styles.inputCluster}>
          {unit === 'imperial' ? (
            <>
              <input
                id="ob-weight"
                className={styles.input}
                type="text"
                inputMode="decimal"
                value={answers.weightLbs ?? ''}
                onChange={(e) => setAnswer({ weightLbs: e.target.value })}
                onBlur={() => setShown((s) => ({ ...s, weight: true }))}
                aria-invalid={shown.weight && weightErr ? true : undefined}
                aria-describedby={shown.weight && weightErr ? 'ob-weight-problem' : undefined}
                placeholder="e.g. 155"
              />
              <span className={styles.unitSuffix}>lbs</span>
            </>
          ) : (
            <>
              <input
                id="ob-weight"
                className={styles.input}
                type="text"
                inputMode="decimal"
                value={answers.weightKg ?? ''}
                onChange={(e) => setAnswer({ weightKg: e.target.value })}
                onBlur={() => setShown((s) => ({ ...s, weight: true }))}
                aria-invalid={shown.weight && weightErr ? true : undefined}
                aria-describedby={shown.weight && weightErr ? 'ob-weight-problem' : undefined}
                placeholder="e.g. 70"
              />
              <span className={styles.unitSuffix}>kg</span>
            </>
          )}
        </div>
        {/* B460 — the sentence's room is always reserved, so it appearing never
            moves the height boxes under it. */}
        <div className={styles.problemSlot}>
          {shown.weight && weightErr && (
            <span id="ob-weight-problem" role="alert" className={styles.fieldProblem}>
              {weightErr}
            </span>
          )}
        </div>
      </div>

      {/* Height */}
      <div className={styles.row}>
        <label className={styles.label}>Height</label>
        <div className={styles.inputCluster}>
          {unit === 'imperial' ? (
            <>
              <input
                className={styles.input}
                type="text"
                inputMode="numeric"
                value={answers.heightFt ?? ''}
                onChange={(e) => setAnswer({ heightFt: e.target.value })}
                onBlur={() => setShown((s) => ({ ...s, height: true }))}
                aria-invalid={shown.height && heightErr ? true : undefined}
                aria-describedby={shown.height && heightErr ? 'ob-height-problem' : undefined}
                placeholder="ft"
                aria-label="Height feet"
              />
              <span className={styles.unitSuffix}>ft</span>
              <input
                className={styles.input}
                type="text"
                inputMode="numeric"
                value={answers.heightIn ?? ''}
                onChange={(e) => setAnswer({ heightIn: e.target.value })}
                onBlur={() => setShown((s) => ({ ...s, height: true }))}
                aria-invalid={shown.height && heightErr ? true : undefined}
                aria-describedby={shown.height && heightErr ? 'ob-height-problem' : undefined}
                placeholder="in"
                aria-label="Height inches"
              />
              <span className={styles.unitSuffix}>in</span>
            </>
          ) : (
            <>
              <input
                className={styles.input}
                type="text"
                inputMode="numeric"
                value={answers.heightCm ?? ''}
                onChange={(e) => setAnswer({ heightCm: e.target.value })}
                onBlur={() => setShown((s) => ({ ...s, height: true }))}
                aria-invalid={shown.height && heightErr ? true : undefined}
                aria-describedby={shown.height && heightErr ? 'ob-height-problem' : undefined}
                placeholder="e.g. 175"
                aria-label="Height centimeters"
              />
              <span className={styles.unitSuffix}>cm</span>
            </>
          )}
        </div>
        <div className={styles.problemSlot}>
          {shown.height && heightErr && (
            <span id="ob-height-problem" role="alert" className={styles.fieldProblem}>
              {heightErr}
            </span>
          )}
        </div>
      </div>

      <div className={styles.infoPanel}>
        Your weight lets your coaches tailor fueling and hydration advice, and
        notice when you may not be eating enough for your training.
        <div className={styles.infoPanelTrust}>Not shared with anyone.</div>
      </div>

      {/* Part 7 (ruling 11): with a value that is refused, Continue is WAITING: pressable, and the
          press names the box. Flag off it is the always-enabled button it was. */}
      <StepCta
        onClick={handleContinue}
        aria-disabled={v2 && (weightErr || heightErr) ? true : undefined}
      >
        Continue
      </StepCta>
      {/* B472 — the shared link sits 4 px under Continue, a mis-tap on the button
          that skips the whole step; this step holds at least 12 px between them. */}
      <div className={styles.skipGap}>
        <TertiaryLink onClick={handleSkip}>
          Skip for now. My coaches can ask later.
        </TertiaryLink>
      </div>
    </>
  );
}
